import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, X } from 'lucide-react';
import { enablePush, getPushState, isPushSupported } from '../lib/push';
import { toast } from 'sonner';

/**
 * Persistent banner shown on Home if push notifications are not active yet.
 * Auto-dismisses if the user grants permission. Can be temporarily dismissed
 * (sessionStorage) but reappears on next login/session.
 */
export default function PushEnableBanner() {
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [blocked, setBlocked] = useState(false);

  const check = async () => {
    if (!isPushSupported()) return setVisible(false);
    if (sessionStorage.getItem('gtc_push_banner_dismissed') === '1') return setVisible(false);
    const s = await getPushState();
    const needs = !(s.subscribed && s.enabled) && !s.blocked;
    setBlocked(s.permission === 'denied');
    // If browser permanently blocked, still show but with different CTA
    setVisible(!(s.subscribed && s.enabled));
  };

  useEffect(() => { check(); const iv = setInterval(check, 8000); return () => clearInterval(iv); }, []);

  const activate = async () => {
    setBusy(true);
    try {
      const res = await enablePush();
      if (res.ok) {
        toast.success('Notifiche attivate! Riceverai gli aggiornamenti automaticamente.');
        setVisible(false);
      } else if (res.reason === 'denied') {
        setBlocked(true);
        toast.error("Permesso negato. Sblocca dalle Impostazioni Safari → Notifiche.");
      } else if (res.reason === 'unsupported') {
        toast.error('Notifiche non supportate. Su iPhone installa l\'app dalla schermata Home.');
      } else if (res.reason === 'sw-failed') {
        toast.error('Service Worker non pronto. Ricarica la pagina.' + (res.error ? ` (${res.error})` : ''));
      } else if (res.reason === 'subscribe-failed') {
        toast.error('Errore iOS/Safari: ' + (res.error || 'sottoscrizione fallita'));
      } else if (res.reason === 'server-save-failed') {
        toast.error('Server non raggiungibile: ' + (res.error || 'riprova'));
      } else if (res.reason === 'no-vapid-key') {
        toast.error('Chiave server non disponibile. Contatta l\'admin.');
      } else {
        toast.error('Impossibile attivare le notifiche' + (res.error ? `: ${res.error}` : ''));
      }
    } finally { setBusy(false); }
  };

  const dismiss = (e) => {
    e.stopPropagation();
    sessionStorage.setItem('gtc_push_banner_dismissed', '1');
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="push-banner"
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          className="relative overflow-hidden rounded-2xl border border-[var(--gold)]/35 bg-gradient-to-r from-[var(--gold)]/15 via-[var(--gold)]/8 to-transparent p-4 flex items-center gap-3"
          data-testid="push-banner"
        >
          <div className="w-11 h-11 rounded-full bg-[var(--gold-soft)] border border-[var(--gold)]/40 flex items-center justify-center flex-shrink-0">
            <Bell className="w-5 h-5 text-[var(--gold)]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-heading text-sm font-medium text-white">
              {blocked ? 'Notifiche bloccate' : 'Attiva le notifiche'}
            </div>
            <div className="text-[11px] text-white/70 leading-snug">
              {blocked
                ? 'Sblocca dal browser (icona 🔒 accanto all\'URL → Autorizzazioni → Notifiche → Consenti)'
                : 'Ricevi subito nuovi messaggi, comunicazioni e novità'}
            </div>
          </div>
          {!blocked && (
            <button
              onClick={activate}
              disabled={busy}
              data-testid="push-banner-cta"
              className="btn-gold px-3 py-2 text-xs whitespace-nowrap flex-shrink-0 disabled:opacity-60"
            >
              {busy ? 'Attivo…' : 'Attiva'}
            </button>
          )}
          <button
            onClick={dismiss}
            data-testid="push-banner-close"
            aria-label="Chiudi"
            className="p-1 text-white/40 hover:text-white/80"
          >
            <X className="w-4 h-4" />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
